import axios from "axios";
import {
  getActiveStoredAccount,
  getStoredAccounts,
  getStoredActiveAccountId,
  upsertStoredAccount,
} from "./accountSession";
import { ACCOUNT_SESSION_HEADER, withSessionHeaders } from "./authRequest";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "/api",
  timeout: 30000,
  withCredentials: true,
});

// Prevent repeated logout alerts
let isSessionExpiredShown = false;
let refreshPromise = null;

export function resetSessionExpiredGuard() {
  isSessionExpiredShown = false;
}

async function refreshActiveSession() {
  const activeAccount = getActiveStoredAccount(
    getStoredAccounts(),
    getStoredActiveAccountId()
  );

  if (!activeAccount?.id || !activeAccount?.session_alias) {
    throw new Error("No refresh session is available.");
  }

  const response = await axios.post(
    `${API.defaults.baseURL}/auth/refresh`,
    {
      account_id: activeAccount.account_id || activeAccount.user?.id || null,
      session_alias: activeAccount.session_alias,
    },
    {
      withCredentials: true,
      timeout: API.defaults.timeout,
      headers: {
        "Content-Type": "application/json",
        [ACCOUNT_SESSION_HEADER]: activeAccount.session_alias,
      },
    }
  );

  upsertStoredAccount(response.data);
  resetSessionExpiredGuard();
  return response.data;
}

API.interceptors.request.use(
  (config) => {
    config.headers = withSessionHeaders(config.headers || {});

    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;

    if (isFormData) {
      delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] =
        config.headers["Content-Type"] || "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    let status = error.response?.status;
    const originalRequest = error.config || {};
    const requestUrl = String(originalRequest.url || "");

    if (
      status === 401 &&
      !originalRequest._retry &&
      !requestUrl.includes("/auth/login") &&
      !requestUrl.includes("/auth/google") &&
      !requestUrl.includes("/auth/refresh")
    ) {
      originalRequest._retry = true;

      try {
        if (!refreshPromise) {
          refreshPromise = refreshActiveSession().finally(() => {
            refreshPromise = null;
          });
        }

        await refreshPromise;
        originalRequest.headers = withSessionHeaders(originalRequest.headers || {});
        return API(originalRequest);
      } catch (refreshError) {
        error = refreshError?.response ? refreshError : error;
        status = error.response?.status;
      }
    }

    if (status === 401) {
      if (!isSessionExpiredShown) {
        isSessionExpiredShown = true;

        const message =
          error.response?.data?.message ||
          "Your session has expired. Please login again.";

        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("session-expired", {
              detail: { message },
            })
          );
        }, 50);
      }
    }

    if (status === 403) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "You do not have permission to perform this action.";

      console.warn("Access denied:", message);
    }

    return Promise.reject(error);
  }
);

export default API;
